// Strategic development contracts (P0) — SPEC_TECH_TREE_PIVOT.md §5.4/§8.1.1.
// The historical revenue bridge across the '93 seed-stage treadmill: a console/
// arcade principal pays monthly NRE while you build (in exchange for dated
// feature obligations, priced P0-lite as a design-EM tax), then committed
// socket royalties inside an authored calendar window, and — for the quad
// gambit — the authored gen-2 cancellation beat: the principal kills the
// follow-up mid-development and pays a walk-away fee that lands exactly when
// the pivot valley is deepest. Signing happens in garage.ts (incorporation).

import type { DevContractDef } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";

export function contractDef(ctx: TickContext): DevContractDef | null {
  const dc = ctx.s.devContract;
  if (!dc) return null;
  return ctx.content.scenario.devContracts?.find((d) => d.id === dc.defId) ?? null;
}

export function processDevContract(ctx: TickContext): void {
  const { s } = ctx;
  const dc = s.devContract;
  if (!dc || dc.cancelled || s.dead) return;
  const def = contractDef(ctx);
  if (!def) return;
  const m = s.monthIndex;

  if (m < (def.nreEndMonth ?? def.royaltyStartMonth)) {
    // NRE phase: the principal pays for development it is steering — and its
    // dated feature obligations consume real design bandwidth (the warp)
    if (def.nreMonthly > 0) {
      const nre = Math.round(def.nreMonthly * ctx.monthsPerTick);
      charge(ctx, "devContract", nre, `${def.principal} — development contract NRE`);
      ctx.nreRevenue += nre;
    }
    if (def.nreEmMonthly > 0) {
      const draw = Math.min(def.nreEmMonthly * ctx.monthsPerTick, ctx.emBudget.design);
      ctx.emBudget.design -= draw;
      ctx.allocation.design.nre += draw;
    }
  } else if (m <= def.royaltyEndMonth && def.royaltyMonthly > 0) {
    // committed socket royalties: real product revenue, on the principal's
    // volume — first money in the field years before retail 3D pays anyone
    const roy = Math.round(def.royaltyMonthly * ctx.monthsPerTick);
    charge(ctx, "socketNre", roy, `${def.principal} — committed socket royalties`);
    ctx.revenue += roy;
  }

  // the authored cancellation beat (the NV2 story)
  if (def.cancelMonth !== undefined && m >= def.cancelMonth) {
    dc.cancelled = true;
    if (def.cancelFee) {
      charge(ctx, "devContract", def.cancelFee, `${def.principal} — walk-away fee on the cancelled follow-up`);
    }
    emit(
      ctx,
      "devContractCancelled",
      `${def.principal} killed the follow-up mid-development. The walk-away fee clears — and the door closes on the revenue stream the company was built on.`,
      { contractId: def.id, fee: def.cancelFee ?? 0 },
    );
  }
}
